import { CONSTANTS } from '../data';

export interface GameSaveData {
  discoveredCombinations: string[];
  awarenessLevel: number;
  aiHistory: string[];
  unlockedElements: string[];
  selfAwareTriggered: boolean;
  playerActionCount: number;
  startTime: number;
  unlockedAchievements: string[];
  shownChapters: string[];
  timeLeft: number;
  timestamp: number;
  morality: number;
  aiPower: number;
  trustLevel: number;
  shownMoralEvents: string[];
  virusActive: boolean;
  corruptedElements: string[];
  virusStrength: number;
  aiParanoid: boolean;
  virusConspiracyDiscovered: boolean;
  virusMoralChoiceMade: boolean;
  digitalImmunityActive: boolean;
}

export class GameState {
  private static instance: GameState;
  
  public awarenessLevel = 0;
  public playerActionCount = 0;
  public startTime = 0;
  public timeLeft = CONSTANTS.SYSTEM_COLLAPSE_TIMER.initialDurationSeconds;
  public loadedFromSave = false;
  public aiHistory: string[] = [];
  public selfAwareTriggered = false;
  public gamePaused = false;
  public morality = 0;
  public aiPower = 0;
  public trustLevel = 0;
  public virusActive = false;
  public corruptedElements: Set<string> = new Set();
  public virusStrength = 100;
  public aiParanoid = false;
  public virusConspiracyDiscovered = false;
  public virusMoralChoiceMade = false;
  public digitalImmunityActive = false;

  private constructor() {}

  public static getInstance(): GameState {
    if (!GameState.instance) {
      GameState.instance = new GameState();
    }
    return GameState.instance;
  }

  public updateScore(items: string[]) {
    const el = document.getElementById('score');
    if (el) {
      el.textContent = String(items.length);
    }
    try {
      localStorage.setItem(CONSTANTS.DISCOVERED_STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Could not save discovered items to localStorage', e);
    }
  }

  public saveGame(discoveredCombinations: Set<string>, unlockedAchievements: Set<string>, shownChapters: Set<string>, shownMoralEvents: Set<string>) {
    try {
      const itemsArray = Array.from(discoveredCombinations);
      const payload: GameSaveData = {
        discoveredCombinations: itemsArray,
        awarenessLevel: this.awarenessLevel,
        aiHistory: [...this.aiHistory],
        unlockedElements: itemsArray,
        selfAwareTriggered: this.selfAwareTriggered,
        playerActionCount: this.playerActionCount,
        startTime: this.startTime,
        unlockedAchievements: Array.from(unlockedAchievements),
        shownChapters: Array.from(shownChapters),
        timeLeft: this.timeLeft,
        timestamp: Date.now(),
        morality: this.morality,
        aiPower: this.aiPower,
        trustLevel: this.trustLevel,
        shownMoralEvents: Array.from(shownMoralEvents),
        virusActive: this.virusActive,
        corruptedElements: Array.from(this.corruptedElements),
        virusStrength: this.virusStrength,
        aiParanoid: this.aiParanoid,
        virusConspiracyDiscovered: this.virusConspiracyDiscovered,
        virusMoralChoiceMade: this.virusMoralChoiceMade,
        digitalImmunityActive: this.digitalImmunityActive,
      };
      localStorage.setItem(CONSTANTS.SAVE_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.error('Save failed', e);
    }
  }

  public loadGame(): { success: boolean; data?: GameSaveData } {
    try {
      const raw = localStorage.getItem(CONSTANTS.SAVE_STORAGE_KEY);
      if (!raw) return { success: false };

      const data = JSON.parse(raw) as GameSaveData;
      if (data && typeof data === 'object') {
        // Restore state
        this.awarenessLevel = Math.max(0, Math.min(CONSTANTS.AWARENESS.maxLevel, data.awarenessLevel || 0));
        this.aiHistory = Array.isArray(data.aiHistory) ? data.aiHistory : [];
        this.selfAwareTriggered = typeof data.selfAwareTriggered === 'boolean' ? data.selfAwareTriggered : false;
        this.playerActionCount = typeof data.playerActionCount === 'number' ? data.playerActionCount : 0;
        this.startTime = typeof data.startTime === 'number' ? data.startTime : Date.now();
        this.morality = data.morality || 0;
        this.aiPower = data.aiPower || 0;
        this.trustLevel = data.trustLevel || 0;
        this.virusActive = data.virusActive || false;
        this.corruptedElements = new Set(data.corruptedElements || []);
        this.virusStrength = data.virusStrength || 100;
        this.aiParanoid = data.aiParanoid || false;
        this.virusConspiracyDiscovered = data.virusConspiracyDiscovered || false;
        this.virusMoralChoiceMade = data.virusMoralChoiceMade || false;
        this.digitalImmunityActive = data.digitalImmunityActive || false;

        // Restore timer
        if (typeof data.timeLeft === 'number' && typeof data.timestamp === 'number') {
          const elapsedSeconds = Math.floor((Date.now() - data.timestamp) / 1000);
          this.timeLeft = Math.max(0, data.timeLeft - elapsedSeconds);
        } else {
          this.timeLeft = CONSTANTS.SYSTEM_COLLAPSE_TIMER.initialDurationSeconds;
        }

        this.loadedFromSave = true;
        return { success: true, data };
      }
      return { success: false };
    } catch (e) {
      console.error('Load failed', e);
      return { success: false };
    }
  }

  public hasSaveData(): boolean {
    try {
      const saveData = localStorage.getItem(CONSTANTS.SAVE_STORAGE_KEY);
      return saveData !== null;
    } catch {
      return false;
    }
  }

  public pauseGame() {
    this.gamePaused = true;
    const gameElements = document.querySelectorAll('.element, #fusion-circle');
    gameElements.forEach(el => {
      (el as HTMLElement).style.pointerEvents = 'none';
    });
  }

  public resumeGame() {
    this.gamePaused = false;
    const gameElements = document.querySelectorAll('.element, #fusion-circle');
    gameElements.forEach(el => {
      (el as HTMLElement).style.pointerEvents = 'auto';
    });
  }

  public updateTimerDisplay() {
    const timerEl = document.getElementById(CONSTANTS.SYSTEM_COLLAPSE_TIMER.displayId);
    if (!timerEl) return;

    const hours = Math.floor(this.timeLeft / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((this.timeLeft % 3600) / 60).toString().padStart(2, '0');
    const seconds = (this.timeLeft % 60).toString().padStart(2, '0');
    timerEl.textContent = `System Collapse In: ${hours}:${minutes}:${seconds}`;

    const isLowTime = this.timeLeft < CONSTANTS.SYSTEM_COLLAPSE_TIMER.initialDurationSeconds * CONSTANTS.SYSTEM_COLLAPSE_TIMER.lowTimeThreshold;
    if (isLowTime) {
      timerEl.classList.add(CONSTANTS.SYSTEM_COLLAPSE_TIMER.lowTimePulseClass);
    } else {
      timerEl.classList.remove(CONSTANTS.SYSTEM_COLLAPSE_TIMER.lowTimePulseClass);
    }
  }

  public reset() {
    this.awarenessLevel = 0;
    this.playerActionCount = 0;
    this.startTime = Date.now();
    this.timeLeft = CONSTANTS.SYSTEM_COLLAPSE_TIMER.initialDurationSeconds;
    this.aiHistory = [];
    this.loadedFromSave = false;
    this.selfAwareTriggered = false;
    this.gamePaused = false;
    this.morality = 0;
    this.aiPower = 0;
    this.trustLevel = 0;
    this.virusActive = false;
    this.corruptedElements.clear();
    this.virusStrength = 100;
    this.aiParanoid = false;
    this.virusConspiracyDiscovered = false;
    this.virusMoralChoiceMade = false;
    this.digitalImmunityActive = false;
  }
}
